import React, { useContext, useEffect, useRef, useState } from 'react';
import { TableContext } from './Table';
import ModalFactory from '../Modal';
import styled from 'styled-components';
import Card from '../forms/Card';
import OverflowTooltip from './OverflowTooltip';
import HamLink from '../forms/HamLink';


const ExpandableRow = ({ row, children, toggleIndex = 0, tableHeaders, addOverflowWrapper }) => {
    const [isOpen, setOpen] = useState(false);

    const colSpan = tableHeaders.filter(e => (e.show ?? true)).length;
    const { children: rowChildren, ...rowProps } = row.props;

    const flatChildren = [rowChildren].flat();

    const hasChildren = !!children;

    const allChildren = flatChildren.map((child, i) => {
        if (!hasChildren) return child;
        if (i !== toggleIndex) return child;
        if (!React.isValidElement(child) || child.type !== 'td') return child;

        return React.cloneElement(child, { ...child.props, key: i },
            <HamLink
                icon={isOpen ? 'chevronDown' : 'chevronRight'}
                onClick={(e) => {
                    e.preventDefault();
                    setOpen(!isOpen);
                }}
            >
                {child.props.children}
            </HamLink>
        );
    });
    const temp = allChildren.map((e, i) => {
        const config = tableHeaders[i] ?? {};
        return {
            show: config.show ?? true,
            order: config.order ?? i,
            child: i === 0 ? e : addOverflowWrapper(e, i),
        };
    });

    const filteredChildren = temp
        .filter((c) => c.show && c.child)
        .sort((a, b) => a.order - b.order)
        .map(c => c.child);

    return (
        <>
            {filteredChildren.length ? React.cloneElement(row, rowProps, filteredChildren) : null}
            {isOpen && (
                <tr>
                    <td colSpan={colSpan} style={{ maxWidth: 'none', overflow: 'visible', padding: '5px 15px', background: '#f0f0f0' }}>
                        <div style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
                            {children}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};


const TableData = ({ renderFormat, ...props }) => {
    const { tableData, currentPage, itemsPerPage, tableHeaders, removeTableLayoutFromMemory, setHeaders, defaultHeaders, tableConfig, tableId } = useContext(TableContext);
    
    useEffect(() => {
        const children = getPage(currentPage);
        if (!tableConfig[tableId]) return;

        if (children.length) {
            console.log(tableHeaders.length, children.length)
            if (tableHeaders.length !== children.length) {
                removeTableLayoutFromMemory();
                setHeaders([...defaultHeaders]);
                ModalFactory.show({
                    title: 'New table Data',
                    body: 'An update to the table has occurred. The saved layouts have been reset.'
                })
            }
        }
    }, [tableData])

    const getPage = (page) => {
        page = (page - 1) * itemsPerPage;
        return tableData
            .slice(page, page + itemsPerPage)
            .map((e, i) => {
                const page = renderFormat(e, i, tableData)
                return page
            })
    }

    const addOverflowWrapper = (c, i) => {
        // If it's not a <td>, return as-is
        if (!React.isValidElement(c) || c.type !== 'td') {
            return c;
        }

        const { children, tooltip, ...props } = c.props;

        return React.cloneElement(
            c,
            { key: i, ...props },
            <OverflowTooltip tooltip={tooltip}>{children}</OverflowTooltip>
        );
    };


    return tableData.length ? (
        <tbody>
            {getPage(currentPage).map((result) => {
                // Handle expandable row objects: { row: <tr>...</tr>, children: <JSX/> }
                if (result && !React.isValidElement(result) && React.isValidElement(result.row)) {
                    return (
                        <ExpandableRow
                            key={result.row.key}
                            row={result.row}
                            toggleIndex={result.toggleIndex}
                            tableHeaders={tableHeaders}
                            addOverflowWrapper={addOverflowWrapper}
                        >
                            {result.children}
                        </ExpandableRow>
                    );
                }

                const { children, ...props } = result.props;

                const temp = [children].flat().map((e, i) => {
                    const config = tableHeaders[i] ?? {}
                    return {
                        show: config.show ?? true,
                        order: config.order ?? i,
                        child: addOverflowWrapper(e, i),
                    }
                })

                const filteredChildren = temp
                .filter((c) => c.show && c.child)
                .sort((a, b) => a.order - b.order)
                .map(c => c.child);

                return filteredChildren.length ? React.cloneElement(result, props, filteredChildren) : null;
            })}
        </tbody>
    ) : null;
}

export default TableData;